import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { NAV_LINKS, CONTACT } from "@/data/content";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      <header
        data-testid="main-header"
        className={`fixed top-0 left-0 right-0 z-50 bg-oxford transition-all duration-500 ${
          scrolled ? "shadow-[0_10px_40px_-15px_rgba(2,10,20,0.8)] border-b border-gold/15" : "border-b border-white/5"
        }`}
      >
        <div className={`max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between transition-all duration-500 ${scrolled ? "h-16" : "h-20 lg:h-24"}`}>
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 shrink-0" aria-label="BRIK Procurement Partners — Inicio">
            <img src="/brand/isotipo-gold.webp" alt="" className={`w-auto transition-all duration-500 ${scrolled ? "h-11" : "h-[52px] lg:h-14"}`} />
            <span className="flex flex-col items-start gap-1">
              <img src="/brand/brik-white.webp" alt="BRIK" className={`w-auto transition-all duration-500 ${scrolled ? "h-5" : "h-6"}`} />
              <span className="font-display text-[8px] sm:text-[9px] font-semibold tracking-[0.42em] uppercase text-steel">
                Procurement Partners
              </span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-7" aria-label="Navegación principal" data-testid="desktop-nav">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.id}
                to={link.path}
                data-testid={`nav-link-${link.id}`}
                className={({ isActive }) =>
                  `nav-link after:absolute after:left-0 after:-bottom-1.5 after:h-px after:bg-gold after:transition-transform after:duration-300 after:origin-left ${
                    isActive ? "text-gold after:w-full after:scale-x-100" : "after:w-full after:scale-x-0 hover:after:scale-x-100"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
            <Link to="/contacto" data-testid="header-cta-hablemos" className="btn-gold !px-6 !py-3 ml-2">
              Contacto
            </Link>
          </nav>

          <button
            data-testid="mobile-menu-button"
            onClick={() => setOpen(!open)}
            className="lg:hidden relative w-10 h-10 flex flex-col items-center justify-center gap-1.5"
            aria-label={open ? "Cerrar menú" : "Abrir menú"}
            aria-expanded={open}
          >
            <span className={`block w-6 h-px bg-gold transition-all duration-300 ${open ? "rotate-45 translate-y-[3.5px]" : ""}`} />
            <span className={`block w-6 h-px bg-gold transition-all duration-300 ${open ? "-rotate-45 -translate-y-[3.5px]" : ""}`} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-nav"
            className="fixed inset-0 z-40 bg-oxford diagonal-lines flex flex-col justify-between pt-28 pb-10 px-8 lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
          >
            <img src="/brand/isotipo-gold.webp" alt="" className="absolute -right-16 -bottom-16 w-72 opacity-[0.05] pointer-events-none" />
            <nav className="flex flex-col gap-2" aria-label="Navegación móvil">
              {NAV_LINKS.map((link, i) => (
                <motion.div
                  key={link.id}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                >
                  <NavLink
                    to={link.path}
                    data-testid={`mobile-nav-link-${link.id}`}
                    className={({ isActive }) =>
                      `flex items-center gap-4 py-3 font-display text-2xl font-semibold tracking-tight transition-colors ${
                        isActive ? "text-gold" : "text-white hover:text-gold"
                      }`
                    }
                  >
                    <span className="text-[10px] font-semibold text-gold/60 tracking-widest">0{i + 1}</span>
                    {link.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <motion.div
              className="flex flex-col gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.5 }}
            >
              <span className="h-px w-16 bg-gold/50" />
              <a href={`mailto:${CONTACT.email}`} className="text-sm text-steel hover:text-gold transition-colors">{CONTACT.email}</a>
              <Link to="/contacto" data-testid="mobile-cta-hablemos" className="btn-gold w-fit">
                Transformemos Compras
              </Link>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
